/**
 * Check the names typed into "Send to someone else" against the staff list
 * the form already loads, BEFORE anything is sent.
 *
 * Case round, finding 2: "jhon fauth", "customer service" and "emily" were
 * sent as typed; the backend matched nobody (or two Emilys) and emailed no
 * one. Here each typed name must resolve to exactly one person, or the form
 * says so and offers the people it could mean. Extra words typed into the
 * name box ("emily adams. emily hassen does not work here") are not a name.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorRouteTargetRead } from '../../services/qcpInvestigations';

export type NameCheck =
  /** Matches one person: send to `name` (the directory spelling). */
  | { kind: 'ok'; typed: string; name: string }
  /** Could be several people: pick one. */
  | { kind: 'several'; typed: string; candidates: string[] }
  /** Matches nobody; `candidates` are close spellings, maybe none. */
  | { kind: 'nobody'; typed: string; candidates: string[] }
  /** Reads as a sentence, not a name. */
  | { kind: 'not_a_name'; typed: string; candidates: string[] };

/** Names longer than this many words are a sentence, not a name. */
const MAX_NAME_WORDS = 4;

/** One typed box → its names: commas, semicolons, "/", "&" and "and" separate. */
export function splitTypedNames(text: string): string[] {
  return text
    .split(/\s*(?:,|;|\/|&|\band\b)\s*/i)
    .map((s) => s.trim())
    .filter(Boolean);
}

function words(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9']+/)
    .filter(Boolean);
}

/** Edit distance where swapping two letters counts once, for "jhon" ~ "john". */
function distance(a: string, b: string): number {
  const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
  );
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[a.length][b.length];
}

/** Every typed word starts a different word of the person's name. */
function wordsMatch(typed: string[], name: string[], close: boolean): boolean {
  const left = [...name];
  return typed.every((w) => {
    const i = left.findIndex((n) =>
      close ? n.startsWith(w) || (w.length >= 4 && distance(w, n) <= 1 + Number(w.length >= 7)) : n.startsWith(w),
    );
    if (i < 0) return false;
    left.splice(i, 1);
    return true;
  });
}

/** People whose name contains a word of `text` (a sentence's likely names). */
function namesMentioned(text: string, staff: DoorRouteTargetRead[]): string[] {
  const ws = new Set(words(text).filter((w) => w.length >= 3));
  return staff.filter((p) => words(p.name).some((w) => ws.has(w))).map((p) => p.name);
}

export function checkTypedName(typed: string, staff: DoorRouteTargetRead[]): NameCheck {
  const t = typed.trim();
  const tw = words(t);
  // A full stop inside, or more words than any name has: not a name.
  if (/[.!?]\s+\S/.test(t) || tw.length > MAX_NAME_WORDS) {
    return { kind: 'not_a_name', typed: t, candidates: namesMentioned(t, staff).slice(0, 6) };
  }
  const exact = staff.find((p) => p.name.trim().toLowerCase() === t.toLowerCase());
  if (exact) return { kind: 'ok', typed: t, name: exact.name };
  // A typed name is one person only when nobody else matches it either way
  // the form shows a match: each typed word starting a word of the name, or
  // the text inside the name (the people chips' filter). "ti" with Tim
  // Gebhardt, Justin and Kristina is three people, not Tim (E2E, proposal 7).
  const lower = t.toLowerCase();
  const matches = staff.filter(
    (p) => wordsMatch(tw, words(p.name), false) || p.name.toLowerCase().includes(lower),
  );
  if (matches.length === 1) return { kind: 'ok', typed: t, name: matches[0].name };
  if (matches.length > 1) {
    return { kind: 'several', typed: t, candidates: matches.map((p) => p.name).slice(0, 8) };
  }
  const close = staff.filter((p) => wordsMatch(tw, words(p.name), true));
  return { kind: 'nobody', typed: t, candidates: close.map((p) => p.name).slice(0, 6) };
}

/** Every name in the box, checked. Empty box → empty list. */
export function checkTypedNames(text: string, staff: DoorRouteTargetRead[]): NameCheck[] {
  const t = text.trim();
  if (!t) return [];
  // A sentence is judged whole: splitting "emily adams. emily hassen does
  // not work here" on "and"/commas would make names out of its pieces.
  if (/[.!?]\s+\S/.test(t)) return [checkTypedName(t, staff)];
  return splitTypedNames(t).map((s) => checkTypedName(s, staff));
}

/** The names to send when every check passed; null while any is unresolved. */
export function resolvedNames(checks: NameCheck[]): string[] | null {
  if (checks.some((c) => c.kind !== 'ok')) return null;
  return checks.map((c) => (c.kind === 'ok' ? c.name : c.typed));
}

/** Replace one typed name with the person picked for it. */
export function pickFor(text: string, typed: string, name: string): string {
  const t = text.trim();
  if (/[.!?]\s+\S/.test(t)) return name;
  const parts = splitTypedNames(t);
  return parts.map((p) => (p === typed ? name : p)).join(', ');
}
